import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import { Headline, Paragraph, Button, InlineLink } from "./ui";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const customizePanelRef = useRef(null);

  useEffect(() => {
    // Check if user has already made a choice
    const userConsent = localStorage.getItem("wtm-cookie-consent");
    if (!userConsent) {
      setIsVisible(true);
    }
  }, []);

  // Prevent background scroll when customize panel is open
  useEffect(() => {
    if (showCustomize) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCustomize]);

  // Ensure scroll is restored when component becomes invisible
  useEffect(() => {
    if (!isVisible) {
      document.body.style.overflow = "unset";
    }
  }, [isVisible]);

  const handleAcceptAll = () => {
    localStorage.setItem("wtm-cookie-consent", "all");
    
    // Update Google Consent Mode v2 BEFORE hiding the banner
    if (typeof window.gtag === 'function') {
      console.log('Updating consent to granted for all...');
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted',
        'personalization_storage': 'granted'
      });
      
      // Also push to dataLayer directly as backup
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'consent_update',
        'consent_type': 'all_granted'
      });
      
      console.log('Consent updated: All cookies accepted');
      console.log('DataLayer after update:', window.dataLayer.slice(-3));
    } else {
      console.warn('gtag not available - consent update failed');
    }
    
    // Small delay before hiding to ensure consent is processed
    setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem("wtm-cookie-consent", "essential-plus-analytics");
    setIsVisible(false);
    
    // Update Google Consent Mode v2 - essential + analytics (for audience building)
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',        // Allow for remarketing audience building
        'ad_user_data': 'granted',      // Allow user data for ads
        'ad_personalization': 'denied', // Still deny personalized ads
        'analytics_storage': 'granted', // Allow analytics
        'personalization_storage': 'denied'
      });
      console.log('Consent updated: Essential accepted');
    } else {
      console.warn('gtag not available - consent update failed');
    }
  };

  const handleCustomize = () => {
    setShowCustomize(!showCustomize);
  };

  const handleSaveFromButton = () => {
    customizePanelRef.current?.handleSave();
  };

  const handleSaveCustom = (preferences) => {
    localStorage.setItem("wtm-cookie-consent", JSON.stringify(preferences));
    setIsVisible(false);
    
    // Update Google Consent Mode v2 based on custom preferences
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'ad_user_data': preferences.marketing ? 'granted' : 'denied',
        'ad_personalization': preferences.marketing ? 'granted' : 'denied',
        'analytics_storage': preferences.statistics ? 'granted' : 'denied',
        'personalization_storage': preferences.preferences ? 'granted' : 'denied'
      });
      console.log('Consent updated: Custom preferences', preferences);
    } else {
      console.warn('gtag not available - consent update failed');
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] bg-[var(--color-primary)] backdrop-blur-md"
      style={{
        height: showCustomize ? "calc(100vh)" : "auto",
      }}
    >
      <div className="mx-auto px-4 py-6 h-full flex flex-col">
        {/* Top Section: Headline + Buttons - Fixed Height */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 flex-shrink-0">
          {!showCustomize ? (
            <div className="flex-1">
              <Paragraph
                variant="small"
                color="secondary"
                className="opacity-90 max-w-6xl text-left"
              >
                Wir verwenden Cookies, um Ihnen das bestmögliche Erlebnis auf
                unserer Website zu bieten. Dazu gehören essenzielle Cookies für
                die Funktionalität sowie optionale Cookies für Analysen.{" "}
                <InlineLink to="/datenschutz" variant="primary">
                  Mehr in unserer Datenschutzerklärung
                </InlineLink>
              </Paragraph>
            </div>
          ) : (
            <div className="flex-1">
              <Headline variant="h2" color="secondary" className="text-left">
                Cookie Einstellungen
              </Headline>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            <Button
              variant="outline"
              size="medium"
              onClick={showCustomize ? handleSaveFromButton : handleCustomize}
            >
              {showCustomize ? "Auswahl akzeptieren" : "Anpassen"}
            </Button>
            {!showCustomize && (
              <Button
                variant="outline"
                size="medium"
                onClick={handleAcceptEssential}
              >
                Essentielle
              </Button>
            )}
            <Button variant="secondary" size="medium" onClick={handleAcceptAll}>
              Alle akzeptieren
            </Button>
          </div>
        </div>

        {/* Bottom Section: Customize Panel - Takes Remaining Space */}
        {showCustomize && (
          <div
            className="flex-1 mt-6 overflow-y-auto min-h-0"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <CustomizePanel
              ref={customizePanelRef}
              onSave={handleSaveCustom}
              onBack={() => setShowCustomize(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CustomizePanel = React.forwardRef(({ onSave, onBack }, ref) => {
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    preferences: true,
    statistics: true,
    marketing: true,
  });
  const [showDetails, setShowDetails] = useState({});

  const handleToggle = (category) => {
    if (category === "necessary") return; // Can't disable necessary
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleDetails = (categoryKey) => {
    setShowDetails((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  const cookieCategories = [
    {
      key: "necessary",
      title: "Notwendig (4)",
      description: "Notwendig für die Grundfunktionen der Website",
      details:
        "Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. Sie umfassen Session-Management, Sicherheitstoken, Cookie-Einstellungen und grundlegende Website-Funktionalität.",
      required: true,
    },
    {
      key: "preferences",
      title: "Präferenzen (2)",
      description: "Speichert Ihre Website-Präferenzen und Einstellungen",
      details:
        "Diese Cookies speichern Ihre persönlichen Einstellungen wie Sprache, Region und andere Präferenzen, um Ihr Benutzererlebnis zu personalisieren.",
    },
    {
      key: "statistics",
      title: "Statistik (3)",
      description: "Google Analytics zur Verbesserung unserer Website",
      details:
        "Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden. Wir verwenden Google Analytics für diese Zwecke.",
    },
    {
      key: "marketing",
      title: "Marketing (2)",
      description: "Für personalisierte Werbung und Social Media",
      details:
        "Diese Cookies werden verwendet, um Ihnen relevante Werbung basierend auf Ihren Interessen zu zeigen und die Effektivität unserer Werbekampagnen zu messen.",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        {cookieCategories.map((category) => (
          <div key={category.key} className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[var(--color-primary)] bg-opacity-10 rounded-lg">
              <div className="flex-1">
                <Paragraph
                  variant="small"
                  color="secondary"
                  className="font-medium"
                >
                  {category.title}
                </Paragraph>
                <button
                  onClick={() => toggleDetails(category.key)}
                  className="text-xs mt-1 opacity-60 hover:opacity-100 transition-opacity underline text-[var(--color-secondary)] bg-transparent border-none cursor-pointer p-0"
                >
                  {showDetails[category.key]
                    ? "Details ausblenden"
                    : "Details anzeigen"}
                </button>
              </div>
              <div className="ml-4">
                {!category.required && (
                  <button
                    onClick={() => handleToggle(category.key)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors focus:outline-none ${
                      preferences[category.key]
                        ? "bg-[var(--color-highlight-yellow)] text-[var(--color-primary)]"
                        : "bg-[var(--color-secondary)] bg-opacity-20 text-[var(--color-primary)]"
                    }`}
                  >
                    {preferences[category.key] ? "AN" : "AUS"}
                  </button>
                )}
              </div>
            </div>
            {showDetails[category.key] && (
              <div className="mt-3 p-3 bg-[var(--color-primary)] bg-opacity-5 rounded">
                <Paragraph
                  variant="small"
                  color="secondary"
                  className="opacity-80"
                >
                  {category.details}
                </Paragraph>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default CookieConsent;
